import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DxDropDownBoxComponent, DxFormComponent, DxHtmlEditorComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { ToastrService } from 'ngx-toastr';
import {
  forkJoin,
  map,
  Observable,
  combineLatest,
  of,
  switchMap,
} from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { VideoUploaderComponent } from 'src/app/components/shared/video-uploader/video-uploader.component';
import { CourseTemplateCreationAndUpdateBaseModel } from 'src/app/models/course/course-template-creation-and-update-base.model';
import { CourseTemplateFullModel } from 'src/app/models/course/course-template-full.model';
import { CourseTemplateQuestionOptionModel } from 'src/app/models/course/course-template-question-option.model';
import { CourseTemplateQuestionModel } from 'src/app/models/course/course-template-question.model';
import { RawFileModel } from 'src/app/models/raw-file-model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { ContentFilePreviewModel } from 'src/app/models/content-file-preview.model';
import { CourseTemplateService } from 'src/app/services/course-template.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { TimeService } from 'src/app/services/time.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import validationEngine from 'devextreme/ui/validation_engine';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TranslateService } from '@ngx-translate/core';
import { LearningTypeModel } from 'src/app/models/training/learning-type.model';
import { LearningContentModel } from 'src/app/models/training/learning-content.model';
import { UserService } from 'src/app/services/user.service';
import { CourseContentTypeModel } from '../../../models/course/course-content-type.model';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { MyCompetenceTrainingModel } from 'src/app/models/training/my-competence-training.model';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { LearningPathModel } from 'src/app/models/learning-paths/learning-path.model';
import { PersonService } from 'src/app/services/person.service';
import { ComplianceCategoryService } from 'src/app/services/compliance-category.service';
import { ComplianceCategoryModel } from 'src/app/models/compliance/compliance-category.model';
import { ComplianceCategoryFullModel } from 'src/app/models/compliance/compliance-category-full.model';
import { CourseTemplateDateModel } from 'src/app/models/course/course-template-date.model';
import { ComplianceCourseDateSelectionComponent } from 'src/app/components/compliance-course-date-selection/compliance-course-date-selection.component';
import { ComplianceRenewalFrequencyModel } from 'src/app/models/compliance/compliance-renewal-frequency.model';
import { PersonBasicSummaryModel } from 'src/app/models/person/person-basic-summary.model';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { FileUtilService } from 'src/app/services/file-util.service';
import { ReportPreviewService } from 'src/app/services/report-preview.service';
import { PreviewResponse } from 'src/app/models/report-preview/preview-response.model';
import { ReportKeyTypeModel } from 'src/app/models/report-preview/report-key-type.model';
import { ReportKeyModel } from 'src/app/models/report-preview/report-key.model';
import { CoursePersonByStateModel } from 'src/app/models/person-states/course-person-by-state.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { PublishingMode } from 'src/app/models/training/publishing-mode.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { EventVisibilityModeModel } from 'src/app/models/event-visibility-mode.model';
import { CoursePersonsTableComponent } from 'src/app/components/course-persons-table/course-persons-table.component';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { TenantContextService } from 'src/app/services/tenant-context.service';

type Question = CourseTemplateQuestionModel & {
  options: (CourseTemplateQuestionOptionModel & { index?: number })[];
  answerIndex: number;
};

interface SelectOption {
  id: string;
  name: string;
  selected?: boolean;
}

@Component({
  selector: 'app-edit-manage-elearning',
  templateUrl: './edit-manage-elearning.component.html',
  styleUrl: './edit-manage-elearning.component.scss',
  standalone: false
})
export class EditManageElearningComponent implements OnInit {
  private timePicker!: ElementRef<HTMLElement>;
  @ViewChild('timePicker') set content(content: ElementRef<HTMLElement>) {
    if (content) {
      this.timePicker = content;
    }
  }

  private complianceDatePicker!: ElementRef<HTMLElement>;
  @ViewChild('complianceDatePicker') 
  set complianceDatePickerContent(content: ElementRef<HTMLElement>) {
    if (content) {
      this.complianceDatePicker = content;
    }
  }

  @ViewChild(DxDropDownBoxComponent, { static: false })
  complianceCategoryDropDown!: DxDropDownBoxComponent;
  
  @ViewChild('complianceCourseDateSelection')
  complianceCourseDateSelectionComponent!: ComplianceCourseDateSelectionComponent;
  
  @ViewChild(CoursePersonsTableComponent) 
  personsTableComponent!: CoursePersonsTableComponent;

  mode: 'edit' | 'view' | 'create' = 'create';

  myCompetenceTrainingModel?: MyCompetenceTrainingModel;

  generalFormData!: {
    name: string;
    description: string | null;
    contentFileId: string | null;
    isDeployed: boolean;
    invitationOnly: boolean;
    descriptionExtended: string | null;
    descriptionCertificate: string | null;
    previewImage: RawFileModel | null;
    customPreviewImage: RawFileModel | null;
    hasCustomPreviewImage: boolean;
    imageFileId: string | null;
    customImageFileId: string | null;
    certificateFooterLocationId: string | null;
  };

  trainingSettingsFormData!: {
    contentFileId: string;
    link: string;
    trainingType: TrainingTypeModel | null;
    publishingMode: PublishingMode;
    complianceCategoryId: string | null;
    date: CourseTemplateDateModel | null;
    iddHours: number;
    iddMinutes: number;
    sendCertificateLink: boolean;
    sendFeedbackLink: boolean;
    invitationOnly: boolean;
    trainingGroupIds: string[];
    learningPathIds: string[];
    trainingCategoryIds: string[];
    organisationUnitLocationIds: string[];
    trainerIds: string[];
    sendInfomailToEmployees: boolean;
    sendInfomailToEmployeesReminderDays: number;
    lernart: LearningTypeModel | null;
    lerninhalt: LearningContentModel | null;
    allowVideoFastForward: boolean;
    visibilityMode: EventVisibilityModeModel | null;
  };

  questionsFormData!: {
    questions: Question[];
  };

  hasCustomPreviewImage: boolean = false;
  isWbdConfigured: boolean = false;
  editingCourseId!: string;
  currentCourse: CourseTemplateFullModel | null = null;
  trainers!: SelectOption[];
  defaultSelectOptions = {
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
  } as DxSelectBoxTypes.Properties;
  learningPathsOptions!: DxSelectBoxTypes.Properties;
  complianceCategoriesOptions!: DxSelectBoxTypes.Properties;
  trainingTypesOptions!: DxSelectBoxTypes.Properties;
  learningTypeOptions!: DxSelectBoxTypes.Properties;
  learningContentOptions!: DxSelectBoxTypes.Properties;
  courseStatusOption: string[];
  contentFilePreview: ContentFilePreviewModel | null = null;
  thumbnailUrl$: Observable<SafeUrl | String | null> = of(null);
  isDescriptionExpanded: boolean = false;
  isCertificateDescriptionExpanded: boolean = false;
  selectedDescriptionTab: number = 0;
  isManager: boolean = false;
  complianceCategoryFull: ComplianceCategoryFullModel | null = null;
  complianceId?: string;
  personsByState: CoursePersonByStateModel | null = null;
  trainingGroups: TrainingGroupModel[] = [];
  trainingCategories: TrainingCategoryModel[] = [];
  personCount: number = 0;

  TrainingType = TrainingTypeModel;

  showPreviewReportViewer = false;
  reportUrl: string = '';

  fileToRemoveId: string | null = null;
  showDeleteFileModal: boolean = false;
  modalConfirmationOptions: ConfirmationDialogData | null = null;
  
  certificateFontFamily: string | null = null;
  certificateFontSize: string | null = null;

  selectedDocument = {
    name: '',
    dataBase64: '',
  };

  descriptionTabs: any[] = [
    {
      id: 0,
      text: this.translateService.instant('TRAINING.FORM.EXTENDED-DESCRIPTION'),
    },
    {
      id: 1,
      text: this.translateService.instant(
        'TRAINING.FORM.TEXT-FOR-CERTIFICATES'
      ),
    },
  ];

  organizationUnits: OrganisationUnitModel[] = [];
  certificateOrganizationUnits: OrganisationUnitModel[] = [];

  isTrainingTimeValid: boolean = true;
  trainingTimeAdapter = {
    getValue: () => {
      return (
        this.trainingSettingsFormData.iddHours * 60 +
        this.trainingSettingsFormData.iddMinutes
      );
    },
  };

  @ViewChild('generalForm') generalForm!: DxFormComponent;
  @ViewChild('trainingSettingsForm') trainingSettingsForm!: DxFormComponent;
  @ViewChild('questionsForm') questionsForm!: DxFormComponent;
  @ViewChild('videoUploaderComponent')
  videoUploaderComponent!: VideoUploaderComponent;

  get isClosed(): boolean {
    return !!(this.currentCourse && this.currentCourse.courseTemplate.isClosed);
  }

  get isViewMode(): boolean {
    return this.mode === 'view' || this.isClosed;
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isComplianceTrainingType(): boolean {
    return this.trainingSettingsFormData.trainingType == TrainingTypeModel.Compliance;
  }

  get isComplianceTrainingTypeWithComplianceCategory(): boolean {
    return this.isComplianceTrainingType && !!this.trainingSettingsFormData.complianceCategoryId;
  }

  get renewalFrequency(): ComplianceRenewalFrequencyModel | undefined {
    if(this.complianceCategoryFull) {
      return this.complianceCategoryFull.complianceCategory.renewalFrequency;
    }

    if(this.currentCourse && this.currentCourse.complianceCategory) {
      return this.currentCourse.complianceCategory.renewalFrequency;
    }

    return undefined;
  }

  get isTrainingCategoryRequired(): boolean {
    const isPublishedToCatalogue =
      this.trainingSettingsFormData.publishingMode == PublishingMode.PublishToCatalogue;

    return isPublishedToCatalogue;
  }

  get isCourseTaken(): boolean {
    return !!(
      this.personsByState?.startedPersons?.length ||
      this.personsByState?.completedPersons?.length
    );
  }

  get isRemoveOptionHidden(): boolean {
    if(this.mode == 'edit') {
      return !this.personsByState || this.isCourseTaken;
    }

    return false;
  }

  get isVisibleToEveryone() {
    return this.isAllOrganizationUnitsSelected &&
      this.trainingSettingsFormData.trainingGroupIds.length == 0
  }

  get isAllOrganizationUnitsSelected() {
    const selectedOrgUnitCount = 
      this.trainingSettingsFormData.organisationUnitLocationIds.length;
      
    return selectedOrgUnitCount == 0
      || selectedOrgUnitCount == this.organizationUnits.length;
  }

  get canDisplayAllowVideoFastForward(): boolean {
    return this.trainingSettingsFormData.trainingType === TrainingTypeModel.General
      && this.isVideoUploaded;
  }

  isVideoUploaded: boolean = false;

  readonly minOptionsCount = 2;

  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;
  
  constructor(
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private courseService: CourseTemplateService,
    private organisationUnitService: OrganisationUnitService,
    private trainingGroupService: TrainingGroupService,
    private learningPathService: LearningPathService,
    private trainingCategoryService: TrainingCategoryService,
    private toastrService: ToastrService,
    private userService: UserService,
    readonly translateService: TranslateService,
    private authorizationService: AuthorizationService,
    private personService: PersonService,
    private reportPreviewService: ReportPreviewService,
    private complianceCategoryService: ComplianceCategoryService,
    private fileUtilService: FileUtilService,
    public userContext: UserContextService,
    private pageStateService: PageStateService,
    private tenantContext: TenantContextService
  ) {
    this.courseStatusOption = [
      this.translateService.instant('COMMON.EVENT-STATUS.DRAFT'),
      this.translateService.instant('COMMON.EVENT-STATUS.DEPLOYED'),
    ];
    this._setInitialFormData();
  }

  public id: string = '';
  public isFileUploading: boolean = false;

  ngOnInit() {
    const { fontFamily, fontSize } = 
      this.tenantContext.getCertificateFontSettings();
    this.certificateFontFamily = fontFamily;
    this.certificateFontSize = fontSize;

    this.authorizationService
      .getCurrentPerson()
      .pipe(first())
      .subscribe((currentUser) => {
        if (currentUser.person.role.name === 'Manager') {
          this.isManager = true;
        }

        combineLatest([
          this.activatedRoute.params,
          this.activatedRoute.queryParams,
        ])
          .pipe(
            switchMap(([param, queryParam]) => {
              const queryParamMode = param['mode'];
              this.complianceId = param['complianceId'];
              this.id = param['id'];

              if (this.id == 'new') {
                this.mode = 'create';

                const data = queryParam['data'];
                if (data) {
                  try {
                    const decoded = decodeURIComponent(data);
                    const parsed = JSON.parse(decoded);

                    this.myCompetenceTrainingModel = parsed;
                  } catch (e) {
                    console.error(
                      'Invalid my competence training data in URL:',
                      e
                    );
                  }
                }
              } else if (!queryParamMode) {
                this.mode = 'view';
                this.editingCourseId = this.id;
              } else if (queryParamMode === 'edit' && this.id) {
                this.mode = 'edit';
              }

              return this.loadingService.load(
                this.isCreateMode
                  ? of(null)
                  : this.courseService.getCourseTemplate(this.id)
              );
            })
          )
          .subscribe((result) => {
            this.currentCourse = result;

            if (!result) {
              if (this.myCompetenceTrainingModel) {

                this.generalFormData = {
                  name: this.myCompetenceTrainingModel.title,
                  description: 
                    this.myCompetenceTrainingModel.shortDescription || null,
                  descriptionExtended: 
                    this.myCompetenceTrainingModel.description || null,
                  contentFileId: null,
                  descriptionCertificate: null,
                  isDeployed: false,
                  invitationOnly: false,
                  previewImage: null,
                  customPreviewImage: null,
                  hasCustomPreviewImage: false,
                  imageFileId: null,
                  customImageFileId: null,
                  certificateFooterLocationId: null
                };

                const url = this.myCompetenceTrainingModel?.previewImage;
                if (!url) return;

                const fileName =
                  this.fileUtilService.getFileNameFromUrl(url) || 'image';

                this.fileUtilService
                  .convertUrlToRawFileModel(url, fileName)
                  .subscribe(raw => {
                    this.generalFormData.previewImage = raw;
                  });
              }

              if(this.complianceId) {
                this.trainingSettingsFormData.complianceCategoryId = this.complianceId;
                this.trainingSettingsFormData.trainingType = TrainingTypeModel.Compliance;
                this.trainingSettingsFormData.publishingMode = PublishingMode.DeployToTargetGroup;

                this.loadComplianceCategoryById(this.complianceId)
              }

              this.loadPageData(null);
              return;
            }

            this.hasCustomPreviewImage = !!result.courseTemplate.customImageFile;
            this.isVideoUploaded = result?.courseTemplate.courseContentType == CourseContentTypeModel.AzureVideo;

            this.questionsFormData = {
              questions: result.questions.map((question) => ({
                ...question,
                answerIndex:
                  question.options.findIndex((opt) => opt.isCorrect) + 1,
                options: question.options.map((opt, i) => ({
                  ...opt,
                  index: i + 1,
                })),
              })),
            };

            if (!this.questionsFormData.questions.length) {
              this.questionsFormData.questions = [this.createEmptyQuestion()];
            }

            this.videoUploaderComponent.hasCustomPreviewImage = this.hasCustomPreviewImage;
            this.videoUploaderComponent.setPreview(result);

            this.generalFormData = {
              name: result.courseTemplate.name,
              description: result.courseTemplate.description || null,
              contentFileId: result.contentFile?.fileId,
              descriptionExtended:
                result.courseTemplate.descriptionExtended || null,
              descriptionCertificate:
                result.courseTemplate.descriptionCertificate || null,
              isDeployed: result.courseTemplate.isDeployed,
              invitationOnly: result.courseTemplate.invitationOnly,
              previewImage: null,
              customPreviewImage: null,
              hasCustomPreviewImage: this.hasCustomPreviewImage,
              imageFileId: result.courseTemplate.imageFile?.fileId ?? null,
              customImageFileId: result.courseTemplate.customImageFile?.fileId ?? null,
              certificateFooterLocationId: result.courseTemplate.certificateFooterLocationId
            };

            const iddHours = TimeService.getHoursFromAbsoluteMinutes(
              result.courseTemplate.iddTimeMinutes || 0
            );
            const iddMinutes = TimeService.getMinutesFromAbsoluteMinutes(
              result.courseTemplate.iddTimeMinutes || 0
            );
            this.trainingSettingsFormData = {
              trainingType: result.courseTemplate.trainingType,
              publishingMode: result.courseTemplate.publishingMode,
              complianceCategoryId: result.courseTemplate.complianceCategoryId,
              date: result.date,
              iddHours,
              iddMinutes,
              sendCertificateLink: result.courseTemplate.sendCertificateLink,
              sendFeedbackLink: result.courseTemplate.sendFeedbackLink,
              invitationOnly: result.courseTemplate.invitationOnly,
              trainingGroupIds: result.trainingGroups.map((group) => group.id),
              learningPathIds: result.learningPaths.map(
                (path) => path.learningPath.id
              ),
              trainingCategoryIds: result.trainingCategories.map(
                (category) => category.id
              ),
              organisationUnitLocationIds: result.organisationUnits.map(
                (unit) => unit.id
              ),
              trainerIds: result.trainers.map((trainer) => trainer.id),
              link: result.courseTemplate.link,
              sendInfomailToEmployees:
                result.courseTemplate.sendInfomailToEmployees,
              sendInfomailToEmployeesReminderDays:
                result.courseTemplate.sendInfomailToEmployeesReminderDays,
              lernart: result.courseTemplate.lernart,
              lerninhalt: result.courseTemplate.lerninhalt,
              contentFileId: result.courseTemplate.contentFileId,
              allowVideoFastForward: result.courseTemplate.allowVideoFastForward,
              visibilityMode: result.courseTemplate.visibilityMode
            };

            this.loadPageData(this.id);

            this.loadingService
              .load(this.videoUploaderComponent.getContentFilePreviewImage$())
              .subscribe((result) => {
                this.generalFormData.previewImage = result;
              });
          });
      });
  }

  duplicateCourse(courseId: string) {
    this.loadingService
      .load(this.courseService.getCourseTemplateClone(courseId))
      .subscribe((courseDuplicate) => {
        this.router.navigate([
          'manage-elearning',
          'edit',
          courseDuplicate.courseTemplate.id,
        ]);
      });
  }

  loadPageData(courseId: string | null): void {
    const pageDataObject: any = {
      organizationUnits: this.organisationUnitService.getAllOrganisationUnits(),
      trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
      learningPaths: this.learningPathService.getAllLearningPaths(),
      trainingCategories:
        this.trainingCategoryService.getAllTrainingCategories(),
      complianceCategories:
        this.complianceCategoryService.getUnassignedComplianceCategories(courseId),
      trainers: this.personService.getAllPersons(),
      isWbdConfigured: this.userService.getWbdConfigured(),
      personsByState: courseId 
        ? this.courseService.getCourseTemplatePersons(courseId)
        : of(null),
      personCount: this.personService.getPersonCount(),
    };

    this.loadingService.load(forkJoin(pageDataObject)).subscribe((result) => {
      const {
        organizationUnits,
        trainingGroups,
        learningPaths,
        trainingCategories,
        complianceCategories,
        trainers,
        isWbdConfigured,
        personsByState,
        personCount
      } = result as {
        organizationUnits: Array<OrganisationUnitModel>;
        trainingGroups: Array<TrainingGroupModel>;
        learningPaths: Array<LearningPathModel>;
        trainingCategories: Array<TrainingCategoryModel>;
        complianceCategories: Array<ComplianceCategoryModel>;
        trainers: Array<PersonBasicSummaryModel>;
        isWbdConfigured: boolean;
        personsByState: CoursePersonByStateModel;
        personCount: number;
      };
      this.isWbdConfigured = isWbdConfigured;
      this.organizationUnits = [...organizationUnits];
      this.personsByState = personsByState;
      this.personCount = personCount;

      if (
        this.trainingSettingsFormData.organisationUnitLocationIds.length ==
          this.organizationUnits.length &&
        this.isViewMode
      ) {
        this.trainingSettingsFormData.organisationUnitLocationIds = ['0'];
        // @ts-ignore
        this.organizationUnits = [
          {
            id: '0',
            name: this.translateService.instant('COMMON.ALL'),
            parentId: null,
            type: 1,
            description: '',
            reportFooter: '',
            imageFile: null,
            topLevel: true,
          },
        ];
      } else {
        this.organizationUnits.forEach((organizationUnit) => {
          if (
            this.trainingSettingsFormData.organisationUnitLocationIds.indexOf(
              organizationUnit.id
            ) > -1
          ) {
            organizationUnit.selected = true;
          }
        });
      }

      this.certificateOrganizationUnits = this.organisationUnitService
        .buildCertificateTree(organizationUnits).map(u => ({
          ...u,
          selected: u.id === this.generalFormData.certificateFooterLocationId
        }));

      this.trainers = trainers.map((trainer) => ({
        id: trainer.id,
        name: `${trainer.firstName} ${trainer.lastName}`,
      }));

      this.trainers.forEach((trainer) => {
        if (this.trainingSettingsFormData.trainerIds.indexOf(trainer.id) > -1) {
          trainer.selected = true;
        }
      });

      this.trainingGroups = trainingGroups;
      const trainingGroupsDataSource = trainingGroups;
      trainingGroupsDataSource.forEach((trainingGroup) => {
        if (
          this.trainingSettingsFormData.trainingGroupIds.indexOf(
            trainingGroup.id
          ) > -1
        ) {
          trainingGroup.selected = true;
        }
      });

      const learningPathsDataSource = learningPaths;
      learningPathsDataSource.forEach((learningPath) => {
        if (
          this.trainingSettingsFormData.learningPathIds.indexOf(
            learningPath.id
          ) > -1
        ) {
          learningPath.selected = true;
        }
      });

      this.trainingCategories = trainingCategories;
      const trainingCategoriesDataSource = trainingCategories;
      trainingCategoriesDataSource.forEach((trainingCategory) => {
        if (
          this.trainingSettingsFormData.trainingCategoryIds.indexOf(
            trainingCategory.id
          ) > -1
        ) {
          trainingCategory.selected = true;
        }
      });

      const complianceCategoriesDataSource = complianceCategories;

     
      this.learningPathsOptions = {
        ...this.defaultSelectOptions,
        placeholder: '',
        dataSource: learningPathsDataSource,
        searchEnabled: true,
      };
      
      this.complianceCategoriesOptions = {
        ...this.defaultSelectOptions,
        placeholder: '',
        dataSource: complianceCategoriesDataSource,
        searchEnabled: true,
      };

      this.trainingTypesOptions = {
        ...this.defaultSelectOptions,
        showClearButton: true,
        dataSource: [
          {
            id: TrainingTypeModel.IDD,
            name: this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.IDD'
            ),
          },
          {
            id: TrainingTypeModel.Compliance,
            name: this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
            ),
          },
          {
            id: TrainingTypeModel.General,
            name: this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
            ),
          },
        ],
        placeholder: '',
        displayExpr: 'name',
        valueExpr: 'id',
        onValueChanged: (e: any) => this.onTrainingTypeChanged(e)
      };

      this.learningTypeOptions = {
        ...this.defaultSelectOptions,
        showClearButton: true,
        dataSource: [
          {
            id: 1,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.FACE-TO-FACE'
            ),
          },
          {
            id: 2,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.INDIVIDUAL-TRAINING'
            ),
          },
          {
            id: 3,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.BLENDED-LEARNING'
            ),
          },
          {
            id: 4,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.CONTROLLED-ELEARNING'
            ),
          },
          {
            id: 5,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.SELF-DIRECTED-ELEARNING'
            ),
          },
          {
            id: 6,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARNING-TYPES.LEARNING-AT-WORK'
            ),
          },
        ],
        placeholder: '',
      };
      this.learningContentOptions = {
        ...this.defaultSelectOptions,
        showClearButton: true,
        dataSource: [
          {
            id: 1,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.PENSION-INSURANCE'
            ),
          },
          {
            id: 2,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.NURSING-INSURANCE'
            ),
          },
          {
            id: 3,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.DAMAGE-INSURANCE'
            ),
          },
          {
            id: 4,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PENSION-PROVISION'
            ),
          },
          {
            id: 5,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PROPERTY'
            ),
          },
          {
            id: 6,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.CROSS-DIVISIONAL'
            ),
          },
          {
            id: 7,
            name: this.translateService.instant(
              'COMMON.FIELDS.LEARING-CONTENT-TYPES.ADVISORY-COMPETENCE'
            ),
          },
        ],
        placeholder: '',
      };

      if (this.isCreateMode) {
        this.trainingSettingsFormData.sendCertificateLink = true;
        this.trainingSettingsFormData.sendFeedbackLink = true;
      }
    });
  }

  onTrainingTypeChanged(e: any) {
    const selectedValue = e.value;

    if(selectedValue === TrainingTypeModel.Compliance) {
      this.trainingSettingsFormData.publishingMode = PublishingMode.DeployToTargetGroup;
    } else {
      this.trainingSettingsFormData.complianceCategoryId = null;
      this.trainingSettingsFormData.date = null;
    }
  }

  onComplianceCategoryValueChanged(e: any): void {
    if (!e.value) {
      this.trainingSettingsFormData.complianceCategoryId = null;
      this.trainingSettingsFormData.date = null;
      this.complianceCategoryFull = null;
    }
  }

  onComplianceCategorySelected(e: any) {
    const id = e?.itemData?.id;
    this.loadComplianceCategoryById(id);
  }
  
  isComplianceCategoryOpen = false;
  loadComplianceCategoryById(id: string): void {
    if (!id) {
      return;
    }

    this.loadingService
      .load(this.complianceCategoryService.getComplianceCategoryFull(id))
      .subscribe((complianceCategoryFull) => {
        this.trainingSettingsFormData.complianceCategoryId = id;
        
        this.complianceCategoryFull = complianceCategoryFull;
        this.trainingSettingsFormData.sendInfomailToEmployeesReminderDays = 
          complianceCategoryFull.complianceCategory.reminderDays;

        this.trainingSettingsFormData.organisationUnitLocationIds = complianceCategoryFull.organisationUnits.map(
          (unit) => unit.id
        );
        this.trainingSettingsFormData.trainingGroupIds = complianceCategoryFull.trainingGroups.map(
          (group) => group.id
        );
        this.trainingSettingsFormData.trainingCategoryIds = complianceCategoryFull.trainingCategories.map(
          (category) => category.id
        );
        this.trainingSettingsFormData.learningPathIds = complianceCategoryFull.learningPaths.map(
          (path) => path.id
        );
        
        this.organizationUnits.forEach((organizationUnit) => {
          if (
            this.trainingSettingsFormData.organisationUnitLocationIds.indexOf(
              organizationUnit.id
            ) > -1
          ) {
            organizationUnit.selected = true;
          } else {
            organizationUnit.selected = false;
          }
        });

        this.trainingGroups.forEach((trainingGroup) => {
          if (
            this.trainingSettingsFormData.trainingGroupIds.indexOf(
              trainingGroup.id
            ) > -1
          ) {
            trainingGroup.selected = true;
          } else {
            trainingGroup.selected = false;
          }
        });

        this.trainingCategories.forEach((trainingCategory) => {
          if (
            this.trainingSettingsFormData.trainingCategoryIds.indexOf(
              trainingCategory.id
            ) > -1
          ) {
            trainingCategory.selected = true;
          } else {
            trainingCategory.selected = false;
          }
        });

        (this.learningPathsOptions.dataSource as any[]).forEach((learningPath) => {
          if (
            this.trainingSettingsFormData.learningPathIds.indexOf(
              learningPath.id
            ) > -1
          ) {
            learningPath.selected = true;
          } else {
            learningPath.selected = false;
          }
        });

        this.setVisibilityMode();
        this.isComplianceCategoryOpen = false;
      });
  }

  private setVisibilityMode() {
    this.trainingSettingsFormData.visibilityMode = this.isVisibleToEveryone
      ? EventVisibilityModeModel.Everyone
      : EventVisibilityModeModel.Restricted;
  }

  handleDropdownAction(e: any): void {
    const actionId = e.itemData.id;

    switch (actionId) {
      case 'add-attendees':
        this.navigateInviteAttendees();
        break;
      case 'show-feedback':
        this.navigateShowFeedback();
        break;
      case 'view-course':
        this.navigateViewCourse();
        break;
      case 'duplicate-course':
        this.duplicateCourse(this.editingCourseId);
        break;
      case 'activity-log':
        this.navigateActivityLog();
        break;
      case 'export-report':
        this.exportPersons();
        break;
      default:
        console.warn(`Unhandled dropdown action identity: ${actionId}`);
        break;
    }
  }


  addQuestion(): void {
    this.questionsFormData.questions.push(this.createEmptyQuestion());
  }

  removeQuestion(): void {
    this.questionsFormData.questions.pop();
  }

  addQuestionOption(questionIndex: number): void {
    this.questionsFormData.questions[questionIndex].options.push({
      option: '',
      isCorrect: false,
      index: this.questionsFormData.questions[questionIndex].options.length + 1,
    });
  }

  removeQuestionOption(questionIndex: number): void {
    if(this.questionsFormData.questions[questionIndex].options.length > this.minOptionsCount) {
      this.questionsFormData.questions[questionIndex].options.pop();
    }
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.trainingSettingsFormData.iddHours = value.hours;
    this.trainingSettingsFormData.iddMinutes = value.minutes;

    const trainingTimeGroupConfig =
      validationEngine.getGroupConfig('trainingTimeGroup');
    if (
      this.trainingSettingsFormData.trainingType === 0 &&
      trainingTimeGroupConfig
    ) {
      const isValid =
        validationEngine.validateGroup('trainingTimeGroup').isValid;

      if (isValid) {
        this.isTrainingTimeValid = true;
      }
    }
  }

  selectCorrectAnswer(questionIndex: number, answerIndex: number) {
    this.questionsFormData.questions[questionIndex].answerIndex = answerIndex;
    this.questionsFormData.questions[questionIndex].options =
      this.questionsFormData.questions[questionIndex].options.map(
        (option: any) => ({
          ...option,
          isCorrect: option.index === answerIndex,
        })
      );
  }

  contentFileChange(
    contentFile: {
      fileId: string;
      file: File;
      preview: RawFileModel;
    } | null
  ) {

    this.isVideoUploaded =
      !!contentFile?.file?.type?.startsWith('video/');

    if (contentFile) {
      const { fileId, file, preview } = contentFile;
      this.generalFormData.contentFileId = fileId;
      
      if (preview) {
        if (this.hasCustomPreviewImage) {
          this.generalFormData.customPreviewImage = preview;
        } else {
          this.generalFormData.previewImage = preview;
        }
      } else {
        if (this.hasCustomPreviewImage) {
          this.generalFormData.previewImage = null;
        } else {
          this.generalFormData.customPreviewImage = null;
          if (!this.generalFormData.previewImage && this.currentCourse?.contentFile.fileId === fileId) { 
            // if no preview image uploaded and the same file
            this.videoUploaderComponent?.setPreview(this.currentCourse!);
          }
        }
      }
    } else {
      this.generalFormData.contentFileId = null;
      this.generalFormData.previewImage = null;
      this.generalFormData.customPreviewImage = null;
    }
  }

  uploadingFile(value: boolean) {
    this.isFileUploading = value;
  }

  submit() {
    let isFormValid = this.isFormValid();

    if(isFormValid) {
      const filteredQuestions = this.questionsFormData.questions
        .map((question) => ({
          question: question.question,
          options: question.options
            .filter((option) => !!option.option?.trim())
            .map((option) => ({
              option: option.option,
              isCorrect: option.isCorrect,
            })),
        }))
        .filter((q) => !!q.question?.trim());

      let iddTimeMinutes = 0;

      if (this.trainingSettingsFormData.trainingType === TrainingTypeModel.IDD) {
        iddTimeMinutes =
          this.trainingSettingsFormData.iddHours! * 60 + this.trainingSettingsFormData.iddMinutes!;
      }

      if(this.isComplianceTrainingTypeWithComplianceCategory && this.complianceCourseDateSelectionComponent) {
        this.trainingSettingsFormData.date = this.complianceCourseDateSelectionComponent.getDate();
      }

      if(
        this.trainingSettingsFormData.publishingMode === PublishingMode.ByInvitationOnly
      ) {
        this.trainingSettingsFormData.organisationUnitLocationIds = [];
        this.trainingSettingsFormData.trainingGroupIds = [];
        this.trainingSettingsFormData.trainingCategoryIds = [];
      } 

      if(this.trainingSettingsFormData.visibilityMode === EventVisibilityModeModel.Everyone) {
        this.trainingSettingsFormData.organisationUnitLocationIds = [];
        this.trainingSettingsFormData.trainingGroupIds = [];
      }

      if(this.trainingSettingsFormData.organisationUnitLocationIds.length == 0) {
        this.trainingSettingsFormData.organisationUnitLocationIds = 
          this.organizationUnits.map(ou => ou.id);
      }

      if(!this.canDisplayAllowVideoFastForward) {
        this.trainingSettingsFormData.allowVideoFastForward = false;
      }

      const baseModel: CourseTemplateCreationAndUpdateBaseModel = {
        ...this.generalFormData,
        organisationUnitLocationIds:
          this.trainingSettingsFormData.organisationUnitLocationIds,
        trainingType: this.trainingSettingsFormData.trainingType!,
        complianceCategoryId:
          this.trainingSettingsFormData.complianceCategoryId,
        date: this.trainingSettingsFormData.date,
        publishingMode: this.trainingSettingsFormData.publishingMode,
        sendCertificateLink: this.trainingSettingsFormData.sendCertificateLink,
        sendFeedbackLink: this.trainingSettingsFormData.sendFeedbackLink,
        invitationOnly: this.trainingSettingsFormData.invitationOnly,
        trainingGroupIds: this.trainingSettingsFormData.trainingGroupIds,
        trainingCategoryIds: this.trainingSettingsFormData.trainingCategoryIds,
        trainerIds: this.trainingSettingsFormData.trainerIds,
        iddTimeMinutes: iddTimeMinutes,
        questions: filteredQuestions,
        lernart: this.trainingSettingsFormData.lernart,
        lerninhalt: this.trainingSettingsFormData.lerninhalt,
        sendInfomailToEmployees:
          this.trainingSettingsFormData.sendInfomailToEmployees,
        sendInfomailToEmployeesReminderDays:
          this.trainingSettingsFormData.sendInfomailToEmployeesReminderDays,
        link: this.trainingSettingsFormData.link || null,
        externalProviderTrainingId: this.myCompetenceTrainingModel?.id || null,
        hasCustomPreviewImage: this.hasCustomPreviewImage,
        allowVideoFastForward: this.trainingSettingsFormData.allowVideoFastForward,
        visibilityMode: this.trainingSettingsFormData.visibilityMode,
      };

      if (baseModel.trainingType !== 0) {
        baseModel.lernart = null;
        baseModel.lerninhalt = null;
      }

      const method$ = this.isCreateMode
        ? this.courseService.addCourseTemplate(baseModel)
        : this.courseService.updateCourseTemplate(
            this.currentCourse!.courseTemplate.id,
            baseModel
          );

      this.loadingService.load(method$).subscribe({
        next: (course) => {
          this.toastrService.success(
            this.translateService.instant('MANAGE-ELEARNING.DETAIL.COURSE-TEMPLATE-SAVED-SUCCESSFULLY')
          );
          this.loadingService.setIsFileUploading(false);

          if (this.isCreateMode) {
            this.navigateViewMode(course.courseTemplate.id);
          } else {
            this.navigateViewMode(this.currentCourse!.courseTemplate.id);
          }
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('MANAGE-ELEARNING.DETAIL.COURSE-TEMPLATE-COULD-NOT-BE-SAVED')
          );
          this.loadingService.setIsFileUploading(false);
        },
      });
    }
  }

  isFormValid(): boolean {
    let isValidationFailed = false;
    const isGeneralFormValid = this.generalForm.instance.validate();
    const isTrainingSettingsFormValid =
      this.trainingSettingsForm.instance.validate();
    const isQuestionsFormValid = this.questionsForm.instance.validate();
    const isTrainingTimeValid =
      this.trainingSettingsFormData.trainingType !== 0
        ? { isValid: true }
        : validationEngine.validateGroup('trainingTimeGroup');
    this.isTrainingTimeValid = isTrainingTimeValid.isValid!;

    if (!isGeneralFormValid.isValid && !isValidationFailed) {
      // @ts-ignore
      isGeneralFormValid.brokenRules[0].validator.focus();
      isValidationFailed = true;
    }

    if (!isTrainingSettingsFormValid.isValid && !isValidationFailed) {
      // @ts-ignore
      isTrainingSettingsFormValid.brokenRules[0].validator.focus();
      isValidationFailed = true;
    }

    if (!isTrainingTimeValid.isValid && !isValidationFailed) {
      this.timePicker.nativeElement.scrollIntoView();
      isValidationFailed = true;
    }

    const group = validationEngine.getGroupConfig('customFields');

    if (group) {
      const customFieldsResult = validationEngine.validateGroup('customFields');

      if (!customFieldsResult.isValid) {
        this.scrollToFirstBrokenRule(customFieldsResult);
        return false;
      }
    }

    if (
      !this.generalFormData.contentFileId &&
      (!this.currentCourse ||
        this.currentCourse.courseTemplate.courseContentType !=
          CourseContentTypeModel.External) &&
      !this.myCompetenceTrainingModel
    ) {
      this.toastrService.error(
        this.translateService.instant('VALIDATIONS.VIDEO-REQUIRED')
      );
      return false;
    }

    if (!this.trainingSettingsFormData.organisationUnitLocationIds.length) {
      this.organizationUnits.forEach((unit) => {
        this.trainingSettingsFormData.organisationUnitLocationIds.push(unit.id);
      });
    }

    let isCourseStartDateValid = true;
    if(this.complianceCourseDateSelectionComponent) {
      isCourseStartDateValid = !!this.complianceCourseDateSelectionComponent.validateDates();
      if(!isCourseStartDateValid) {
        this.complianceDatePicker.nativeElement.scrollIntoView();
      }
    }

    if (
      !isGeneralFormValid.isValid ||
      !isQuestionsFormValid.isValid ||
      !isTrainingSettingsFormValid.isValid ||
      !isTrainingTimeValid.isValid ||
      !isCourseStartDateValid
    ) {
      this.toastrService.error(
        this.translateService.instant('VALIDATIONS.VALUES-NOT-VALID')
      );
      return false;
    }

    const filteredQuestions = this.questionsFormData.questions
      .map((question) => ({
        question: question.question,
        options: question.options
          .filter((option) => !!option.option?.trim())
          .map((option) => ({
            option: option.option,
            isCorrect: option.isCorrect,
          })),
      }))
      .filter((q) => !!q.question?.trim());

    const hasIncompleteOptions = filteredQuestions.some(
      (q) => q.options.length < this.minOptionsCount
    );

    if (hasIncompleteOptions) {
      this.toastrService.error(
        this.translateService.instant('MANAGE-ELEARNING.DETAIL.QUESTION_OPTIONS_INCOMPLETE')
      );
      return false;
    }

    return !isValidationFailed;
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }

  navigateBack(): void {
    if (this.isManager) {
      this.router.navigate(['dashboard']);
    } else {
      if(this.complianceId) {
        this.router.navigate([
          'manage-compliance', this.complianceId
        ]);
      } else {
        this.pageStateService.setFlag(
          PageStateFlags.RestoreCourseTemplatesOverview
        );

        this.router.navigate(['manage-elearning']);
      }
    }
  }

  navigateInviteAttendees(): void {
    this.router.navigate([
      'elearning',
      'invite',
      this.currentCourse?.courseTemplate.id,
    ]);
  }

  navigateShowFeedback(): void {
    this.router.navigate([
      'elearning',
      'feedback',
      this.currentCourse?.courseTemplate.id,
    ]);
  }

  navigateViewCourse(): void {
    this.router.navigate([
      'my-trainings',
      'course',
      this.currentCourse?.courseTemplate.id,
    ]);
  }

  navigateActivityLog() {
    this.router.navigate(['activity-log'], {
      queryParams: { courseId: this.currentCourse?.courseTemplate.id },
    });
  }


  navigateViewMode(id: string): void {
    if(this.complianceId){
      this.router.navigate(['manage-compliance', this.complianceId, 'manage-elearning', id]);
    } else {
      this.router.navigate(['manage-elearning', id]);
    }
  }

  navigateToEdit(): void {
    if(this.complianceId){
      this.router.navigate([
        'manage-compliance', 
        this.complianceId, 
        'manage-elearning', 
        'edit',
        this.currentCourse?.courseTemplate.id,
      ]);
    } else {
      this.router.navigate([
        'manage-elearning',
        'edit',
        this.currentCourse?.courseTemplate.id,
      ]);
    }
  }

  private createEmptyQuestion(): Question {
    return {
      question: '',
      answerIndex: 1,
      options: new Array(3).fill(1).map((_, i) => ({
        option: '',
        isCorrect: i === 0,
        index: i + 1,
      })),
    };
  }

  private _setInitialFormData() {
    this.questionsFormData = {
      questions: [this.createEmptyQuestion()],
    };

    this.generalFormData = {
      name: '',
      description: null,
      contentFileId: null,
      descriptionExtended: null,
      descriptionCertificate: null,
      isDeployed: false,
      invitationOnly: false,
      previewImage: null,
      customPreviewImage: null,
      hasCustomPreviewImage: false,
      imageFileId: null,
      customImageFileId: null,
      certificateFooterLocationId: null
    };

    this.trainingSettingsFormData = {
      contentFileId: '',
      trainingType: null,
      complianceCategoryId: null,
      date: null,
      publishingMode: PublishingMode.ByInvitationOnly,
      iddHours: 0,
      iddMinutes: 0,
      sendCertificateLink: false,
      sendFeedbackLink: false,
      invitationOnly: false,
      trainingGroupIds: [],
      trainingCategoryIds: [],
      learningPathIds: [],
      organisationUnitLocationIds: [],
      trainerIds: [],
      // field that don't exist yet
      link: '',
      sendInfomailToEmployees: false,
      sendInfomailToEmployeesReminderDays: 0,
      lernart: null,
      lerninhalt: null,
      allowVideoFastForward: false,
      visibilityMode: null
    };
  }

  validateTrainingTime(e: any) {
    return e.value < 15 ? false : true;
  }

  protected readonly CourseContentTypeModel = CourseContentTypeModel;

  onDescriptionTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedDescriptionTab = e.itemData.id;
  }

  onAuthorSelected(e: any) {
    if (e.node.selected) {
      this.trainingSettingsFormData.trainerIds = [
        ...this.trainingSettingsFormData.trainerIds,
        e.node.key,
      ];
    } else {
      this.trainingSettingsFormData.trainerIds = this.trainingSettingsFormData.trainerIds.filter(
        (id) => id !== e.node.key
      );
    }
  }


  showCertificatePreview() {
    if(this.isViewMode && this.editingCourseId) {
      const key: ReportKeyModel = {
        type: ReportKeyTypeModel.Event,
        id: this.editingCourseId
      };

      const encoded = btoa(JSON.stringify(key));

      this.reportUrl = `CertificateReport:${encoded}`;
      this.showPreviewReportViewer = true;
    } else {
      let isFormValid = this.isFormValid();

      if(isFormValid) {
        const filteredQuestions = this.questionsFormData.questions
          .map((question) => ({
            question: question.question,
            options: question.options
              .filter((option) => !!option.option?.trim())
              .map((option) => ({
                option: option.option,
                isCorrect: option.isCorrect,
              })),
          }))
          .filter((q) => !!q.question?.trim());

        let iddTimeMinutes = 0;

        if (this.trainingSettingsFormData.trainingType === TrainingTypeModel.IDD) {
          iddTimeMinutes =
            this.trainingSettingsFormData.iddHours! * 60 + this.trainingSettingsFormData.iddMinutes!;
        }

        const baseModel: CourseTemplateCreationAndUpdateBaseModel = {
          ...this.generalFormData,
          organisationUnitLocationIds:
            this.trainingSettingsFormData.organisationUnitLocationIds,
          trainingType: this.trainingSettingsFormData.trainingType!,
          complianceCategoryId: this.trainingSettingsFormData.complianceCategoryId,
          date: this.trainingSettingsFormData.date,
          publishingMode: this.trainingSettingsFormData.publishingMode,
          sendCertificateLink: this.trainingSettingsFormData.sendCertificateLink,
          sendFeedbackLink: this.trainingSettingsFormData.sendFeedbackLink,
          invitationOnly: this.trainingSettingsFormData.invitationOnly,
          trainingGroupIds: this.trainingSettingsFormData.trainingGroupIds,
          trainingCategoryIds: this.trainingSettingsFormData.trainingCategoryIds,
          trainerIds: this.trainingSettingsFormData.trainerIds,
          iddTimeMinutes: iddTimeMinutes,
          questions: filteredQuestions,
          lernart: this.trainingSettingsFormData.lernart,
          lerninhalt: this.trainingSettingsFormData.lerninhalt,
          sendInfomailToEmployees:
            this.trainingSettingsFormData.sendInfomailToEmployees,
          sendInfomailToEmployeesReminderDays:
            this.trainingSettingsFormData.sendInfomailToEmployeesReminderDays,
          link: this.trainingSettingsFormData.link || null,
          externalProviderTrainingId: this.myCompetenceTrainingModel?.id || null,
          hasCustomPreviewImage: this.hasCustomPreviewImage,
          allowVideoFastForward: this.trainingSettingsFormData.allowVideoFastForward,
          visibilityMode: this.trainingSettingsFormData.visibilityMode,
        };

        if (baseModel.trainingType !== 0) {
          baseModel.lernart = null;
          baseModel.lerninhalt = null;
        }

        this.loadingService
          .load(
            this.reportPreviewService.createCoursePreview(baseModel)
          )
          .subscribe((res: PreviewResponse) => {
            const key: ReportKeyModel = {
              type: ReportKeyTypeModel.Preview,
              id: res.previewId
            };

            const encoded = btoa(JSON.stringify(key));

            this.reportUrl = `CertificateReport:${encoded}`;
            this.showPreviewReportViewer = true;
          })
      }
    }
  }

  onDocumentSelected(files: any) {
    if(!this.currentCourse) return;

    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.selectedDocument.name = file.name;
      this.selectedDocument.dataBase64 = reader!
        .result!.toString()
        .split(',')[1];

      this.loadingService
        .load(
          this.courseService.addCourseFile(
            this.currentCourse!.courseTemplate.id,
            this.selectedDocument
          )
        )
        .subscribe((file) => {
          this.selectedDocument.name = '';
          this.selectedDocument.dataBase64 = '';
          this.currentCourse?.files.push(file);
        });
    };
    reader.readAsDataURL(file);
  }

  onRemoveFileClicked(file: any) {
    this.showDeleteFileModal = true;
    this.fileToRemoveId = file.fileId;

    this.modalConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'MANAGE-ELEARNING.DETAIL.DELETE-COURSE-FILE.TITLE'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
  }

  onRemoveFileConfirm() {
    if(!this.currentCourse) return;

    this.loadingService
      .load(
        this.courseService.deleteCourseFile(
          this.currentCourse!.courseTemplate!.id,
          this.fileToRemoveId!
        )
      )
      .subscribe(() => {
        this.showDeleteFileModal = false;
        this.currentCourse!.files =
          this.currentCourse!.files.filter(
            (f) => f.fileId !== this.fileToRemoveId!
          );
      });
  }

  onTemplateContentChanged(content: string) {
    this.generalFormData.descriptionExtended = content;
  }

  onPublishingModeChanged(publishingMode: PublishingMode) {
    this.trainingSettingsFormData.publishingMode = publishingMode;

    if (
      publishingMode === PublishingMode.DeployToTargetGroup ||
      publishingMode === PublishingMode.PublishToSubscribers ||
      publishingMode === PublishingMode.PublishToCatalogue
    ) {
        this.trainingSettingsFormData.visibilityMode ??= 
          EventVisibilityModeModel.Everyone;
    } else {
      this.trainingSettingsFormData.visibilityMode = null;
    }
  }

  async exportPersons(): Promise<void> {
    if (!this.personsByState) {
      return;
    }

    const workbook = new Workbook();

    this.personsTableComponent.exportAllPersons(
      workbook,
      this.personsByState
    );

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }),
      'course-persons.xlsx'
    );
  }
}
